import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  type CampaignMixAllocation,
  type ContentPillarAllocation,
  CAMPAIGN_MIX_METADATA,
  CONTENT_PILLAR_METADATA,
} from "@runcheetah/shared";

import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import { Slider } from "@runcheetah/ui";
import { applyNormalizedWeights, percentagesToWeights } from "@/lib/distribution";
import { trpc } from "@/lib/trpc";

export function StrategySection({ brandId }: { brandId: string }) {
  const utils = trpc.useUtils();
  const strategyQuery = trpc.admin.strategy.get.useQuery({ brandId });
  const updateMutation = trpc.admin.strategy.update.useMutation({
    onSuccess: async () => {
      await utils.admin.strategy.get.invalidate({ brandId });
    },
  });
  const regenerateMutation = trpc.admin.strategy.regenerateReasoning.useMutation({
    onSuccess: async () => {
      await utils.admin.strategy.get.invalidate({ brandId });
    },
  });
  const regenerateFullMutation = trpc.admin.strategy.regenerateFull.useMutation({
    onSuccess: async () => {
      setIsDirty(false);
      await utils.admin.strategy.get.invalidate({ brandId });
    },
  });

  const [postsPerDay, setPostsPerDay] = useState(1);
  const [contentPillars, setContentPillars] = useState<ContentPillarAllocation[]>([]);
  const [campaignMix, setCampaignMix] = useState<CampaignMixAllocation[]>([]);
  const [contentPillarWeights, setContentPillarWeights] = useState<number[]>([]);
  const [campaignMixWeights, setCampaignMixWeights] = useState<number[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const strategy = strategyQuery.data ?? null;

  useEffect(() => {
    if (!strategy || isDirty) {
      return;
    }

    setPostsPerDay(strategy.postsPerDay);
    setContentPillars(strategy.contentPillars);
    setCampaignMix(strategy.campaignMix);
    setContentPillarWeights(
      percentagesToWeights(strategy.contentPillars.map((item) => item.percentage)),
    );
    setCampaignMixWeights(
      percentagesToWeights(strategy.campaignMix.map((item) => item.percentage)),
    );
  }, [isDirty, strategy]);

  if (strategyQuery.isPending) {
    return <Skeleton className="h-80" />;
  }

  if (!strategy) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Strategy</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No strategy exists for this brand yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-fit w-full min-w-0 overflow-hidden">
      <CardHeader className="border-b bg-muted/20">
        <CardTitle className="text-base font-semibold">Strategy</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 p-4 sm:p-5">
        <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-[220px_260px_1fr]">
          <div className="min-w-0 space-y-2">
            <span className="text-xs text-muted-foreground">Posts per day</span>
            <Input
              type="number"
              min={1}
              value={postsPerDay}
              onChange={(event) => {
                setIsDirty(true);
                setPostsPerDay(Number(event.target.value) || 1);
              }}
            />
          </div>
          <div className="min-w-0 rounded-2xl border bg-background p-3">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="text-sm font-medium capitalize">{strategy.status}</div>
            <div className="text-xs text-muted-foreground capitalize">
              Reasoning: {strategy.reasoningState}
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-4 xl:grid-cols-2">
          <div className="min-w-0 space-y-3 rounded-2xl border bg-background p-4">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Content pillars
            </span>
            {contentPillars.map((item, index) => (
              <div
                key={item.key}
                className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-3 border-t pt-3 first:border-t-0 first:pt-0"
              >
                {(() => {
                  const meta = CONTENT_PILLAR_METADATA[item.key] ?? {
                    label: item.key.replaceAll("_", " "),
                    description:
                      "Legacy strategy value. Update this distribution to use the current set.",
                  };

                  return (
                    <div className="min-w-0">
                      <div className="text-sm">{meta.label}</div>
                      <div className="text-xs text-muted-foreground">{meta.description}</div>
                      <Slider
                        min={0}
                        max={100}
                        step={1}
                        value={[contentPillarWeights[index] ?? item.percentage]}
                        onValueChange={(nextValues: number[]) => {
                          setIsDirty(true);
                          setContentPillarWeights((current) => {
                            const nextWeights = current.map((value, currentIndex) =>
                              currentIndex === index ? (nextValues[0] ?? item.percentage) : value,
                            );
                            setContentPillars((existing) =>
                              applyNormalizedWeights(existing, nextWeights),
                            );
                            return nextWeights;
                          });
                        }}
                        className="mt-3"
                      />
                    </div>
                  );
                })()}
                <Badge variant="outline">{item.percentage}%</Badge>
              </div>
            ))}
          </div>
          <div className="min-w-0 space-y-3 rounded-2xl border bg-background p-4">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Campaign mix
            </span>
            {campaignMix.map((item, index) => (
              <div
                key={item.key}
                className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-3 border-t pt-3 first:border-t-0 first:pt-0"
              >
                {(() => {
                  const meta = CAMPAIGN_MIX_METADATA[item.key] ?? {
                    label: item.key.replaceAll("_", " "),
                    description:
                      "Legacy strategy value. Update this distribution to use the current set.",
                  };

                  return (
                    <div className="min-w-0">
                      <div className="text-sm">{meta.label}</div>
                      <div className="text-xs text-muted-foreground">{meta.description}</div>
                      <Slider
                        min={0}
                        max={100}
                        step={1}
                        value={[campaignMixWeights[index] ?? item.percentage]}
                        onValueChange={(nextValues: number[]) => {
                          setIsDirty(true);
                          setCampaignMixWeights((current) => {
                            const nextWeights = current.map((value, currentIndex) =>
                              currentIndex === index ? (nextValues[0] ?? item.percentage) : value,
                            );
                            setCampaignMix((existing) =>
                              applyNormalizedWeights(existing, nextWeights),
                            );
                            return nextWeights;
                          });
                        }}
                        className="mt-3"
                      />
                    </div>
                  );
                })()}
                <Badge variant="outline">{item.percentage}%</Badge>
              </div>
            ))}
          </div>
        </div>

        {strategy.reasoning ? (
          <div className="min-w-0 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Explanation
            </span>
            <div className="rounded-2xl border bg-muted/30 p-4">
              <p className="mb-3 whitespace-pre-wrap text-sm font-medium">
                {strategy.reasoning.summary}
              </p>
              <div className="prose prose-sm max-w-none text-sm dark:prose-invert">
                <ReactMarkdown>{strategy.reasoning.bulletsMarkdown}</ReactMarkdown>
              </div>
            </div>
          </div>
        ) : null}

        {strategy.validationIssues.length > 0 ? (
          <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive">
            {strategy.validationIssues.join(" ")}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() =>
              updateMutation.mutate({
                brandId,
                strategy: { postsPerDay, contentPillars, campaignMix },
              })
            }
            disabled={updateMutation.isPending}
          >
            Save Strategy
          </Button>
          <Button
            variant="outline"
            onClick={() => regenerateMutation.mutate({ brandId })}
            disabled={regenerateMutation.isPending}
          >
            Refresh Explanation
          </Button>
          <Button
            variant="outline"
            onClick={() => regenerateFullMutation.mutate({ brandId })}
            disabled={regenerateFullMutation.isPending}
          >
            Regenerate Strategy
          </Button>
          {isDirty ? (
            <Button variant="ghost" disabled>
              Unsaved changes
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
